import React, { useState, useCallback, useEffect } from "react";
import { Box } from "@mui/material";
import { days } from "../../utils/constants";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { addShiftDuty } from "../../features/shift/shiftSlice";
import { updateShiftDuty } from "../../features/shift/shiftSlice";
import AppButton from "../common/AppButton";
import LoadingSpinner from "../common/Loading";
import { Dropdown } from "../common/DropDown";
import { MultiSelectComponent } from "../common/MultiSelectComponent";
import { TimePickerComponent } from "../common/TimePickerComponent";
import { styles } from "./styles";

interface IProps {
  listMembers?: any;
  editMode?: boolean;
  editMember?: any;
}

const AssignShiftContent = ({ listMembers, editMode, editMember }: IProps) => {
  const [state, setState] = useState({
    staffMember: editMode ? editMember?.staffMember : { name: "", id: 0 },
    shiftType: editMode ? editMember?.shiftType?.split(" ")[0] || "" : "",
    startTime: editMode ? `${editMember?.startTime}` : "",
    startTimePeriod: editMode ? editMember?.startTimePeriod : "AM",
    endTime: editMode ? `${editMember?.endTime}` : "",
    endTimePeriod: editMode ? editMember?.endTimePeriod : "AM",
    offDays:
      editMode && editMember?.offDays != null
        ? editMember?.offDays?.map((day: any) => ({ title: day }))
        : [],
    errors: {
      staffMember: "",
      shiftType: "",
      startTime: "",
      endTime: "",
      offDays: "",
    },
  });

  const validateFields = () => {
    const errors: any = {};
    errors.staffMember = state.staffMember.id === 0 ? "*Required" : "";
    errors.shiftType =
      state.shiftType === "" ||
        state.shiftType === undefined ||
        state.shiftType === null
        ? "*Required"
        : "";
    errors.startTime =
      state.startTime === "" ||
        state.startTime === undefined ||
        state.startTime === null
        ? "*Required"
        : "";
    errors.endTime =
      state.endTime === "" ||
        state.endTime === undefined ||
        state.endTime === null
        ? "*Required"
        : "";
    setState((prevState) => ({ ...prevState, errors }));
    return !Object.values(errors).some((error) => error !== "");
  };

  useEffect(() => {
    // Ensure the endTime and startTime are in the expected format
    const hoursEnd = Number(state?.endTime?.split(":")?.[0]);
    const minutesEnd = Number(state?.endTime?.split(":")?.[1]);
    // Only adjust end time if necessary
    if (hoursEnd >= 13) {
      const hours = hoursEnd - 12;
      let formattedHours = hours < 10 ? `0${hours}` : `${hours}`;
      let formattedMinutes =
        minutesEnd < 10 ? `0${minutesEnd}` : `${minutesEnd}`;
      setState((prevState: any) => ({
        ...prevState,
        endTime: `${formattedHours}:${formattedMinutes}`,
        endTimePeriod: "PM",
      }));
    }
    const hoursStart = Number(state?.startTime?.split(":")?.[0]);
    const minutesStart = Number(state?.startTime?.split(":")?.[1]);
    // Only adjust start time if necessary
    if (hoursStart >= 13) {
      const hours = hoursStart - 12;
      let formattedHours = hours < 10 ? `0${hours}` : `${hours}`;
      let formattedMinutes =
        minutesStart < 10 ? `0${minutesStart}` : `${minutesStart}`;
      setState((prevState: any) => ({
        ...prevState,
        startTime: `${formattedHours}:${formattedMinutes}`,
        startTimePeriod: "PM",
      }));
    }
  }, [
    state.startTime,
    state.endTime,
    state.startTimePeriod,
    state.endTimePeriod,
  ]);

  const shiftTypes = ["Morning", "Evening", "Night"];
  const pharmacyID = Number(localStorage?.getItem("pharmacyId"));
  const dispatch = useAppDispatch();
  const { isAddShiftDutyLoading, isUpdateShiftDutyLoading }: any =
    useAppSelector((state) => state.shift);
  useEffect(() => {
    validateFields();
  }, [state.staffMember, state.shiftType, state.startTime, state.endTime]);
  const addShift = useCallback(() => {
    if (!validateFields()) return;
    let startDate;
    let endDate;
    if (state.startTime.includes("AM") || state.startTime.includes("PM")) {
      startDate = `${state.startTime}`;
    } else {
      startDate = `${state.startTime?.split(" ")[0]} ${state.startTimePeriod}`;
    }
    if (state.endTime.includes("AM") || state.endTime.includes("PM")) {
      endDate = `${state.endTime}`;
    } else {
      endDate = `${state.endTime?.split(" ")[0]} ${state.endTimePeriod}`;
    }
    const offDaysArray = state.offDays?.map((item: any) => item?.title);
    const payload = {
      start_time: startDate,
      end_time: endDate,
      shift_type:
        state.shiftType === "Morning"
          ? 0
          : state.shiftType === "Evening"
            ? 1
            : 2,
      off_day: offDaysArray,
      staff_id: state?.staffMember?.id, // Use the id from staffMember object
      pharmacy_id: pharmacyID, // Use pharmacyId from localStorage or state
      // pharmacy_id: 15,
    };
    dispatch(addShiftDuty(payload));
  }, [dispatch, state, pharmacyID]);

  const editShift = useCallback(() => {
    if (!validateFields()) return;
    let startDate;
    let endDate;
    startDate = `${state.startTime?.split(" ")[0]} ${state.startTimePeriod}`;
    endDate = `${state.endTime?.split(" ")[0]} ${state.endTimePeriod}`;
    const offDaysArray = state.offDays?.map((item: any) => item?.title);
    const payload = {
      start_time: startDate,
      end_time: endDate,
      shift_type:
        state.shiftType === "Morning"
          ? 0
          : state.shiftType === "Evening"
            ? 1
            : 2,
      off_day: offDaysArray,
      id: Number(editMember?.dutyID),
    };
    dispatch(updateShiftDuty(payload));
  }, [dispatch, state, editMember]);

  const handleChange = (type: string) => (newValue: any) => {
    switch (type) {
      case "dropdown1":
        setState((prevState: any) => ({
          ...prevState,
          staffMember: {
            name: newValue?.label,
            id: newValue?.value,
          },
        }));
        break;
      case "dropdown2":
        setState((prevState: any) => ({
          ...prevState,
          shiftType: newValue,
        }));
        break;
      case "dropdown3":
        setState((prevState: any) => ({
          ...prevState,
          offDays: newValue,
        }));
        break;
      case "timePicker1":
        setState((prevState: any) => ({
          ...prevState,
          startTime: newValue,
        }));
        break;
      case "timePicker2":
        setState((prevState: any) => ({
          ...prevState,
          endTime: newValue,
        }));
        break;
      case "timePickerPeriod1":
        setState((prevState: any) => ({
          ...prevState,
          startTimePeriod: newValue,
        }));
        break;
      case "timePickerPeriod2":
        setState((prevState: any) => ({
          ...prevState,
          endTimePeriod: newValue,
        }));
        break;
      default:
        break;
    }
  };

  return (
    <Box>
      {(isAddShiftDutyLoading || isUpdateShiftDutyLoading) && (
        <LoadingSpinner fullScreen />
      )}
      <Box sx={styles.modelRow1}>
        <Dropdown
          disabled={editMode ? true : false}
          placeholder={editMode ? "" : "Staff Member"}
          label="Select Staff Member"
          error={state.errors.staffMember}
          errorMessage={state.errors.staffMember}
          options={
            listMembers
              ? listMembers.map((item: any) => ({
                label: item?.name, // The text displayed in the dropdown
                value: item?.id, // The id that will be used as the value
              }))
              : []
          }
          value={state.staffMember?.name}
          onChange={(newValue: any) => handleChange("dropdown1")(newValue)}
        />
        <Dropdown
          label="Select Shift Type"
          placeholder={editMode ? "" : "Shift Type"}
          error={state.errors.shiftType}
          errorMessage={state.errors.shiftType}
          options={shiftTypes}
          value={state.shiftType}
          onChange={(newValue: any) => handleChange("dropdown2")(newValue)}
        />

        <MultiSelectComponent
          label="Select Off Days"
          placeholder={editMode ? "" : state.offDays?.length > 0 ? "" : "Off Days"}
          options={days}
          value={state.offDays}
          onChange={(newValue: any) => handleChange("dropdown3")(newValue)}
        />
      </Box>
      <Box sx={styles.modelRow2}>
        <TimePickerComponent
          value={state.startTime}
          Label={"Start Time"}
          placeholder={"Start Time"}
          valuePeriod={state.startTimePeriod}
          onChange={(value: any) => {
            handleChange("timePicker1")(value);
          }}
          onChangePeriod={(value: any) => {
            handleChange("timePickerPeriod1")(value);
          }}
          error={state.errors.startTime}
          errorMessage={state.errors.startTime}
        />
        <TimePickerComponent
          value={state.endTime}
          valuePeriod={state.endTimePeriod}
          placeholder={"End Time"}
          Label={"End Time"}
          onChange={(value: any) => {
            handleChange("timePicker2")(value);
          }}
          onChangePeriod={(value: any) => {
            handleChange("timePickerPeriod2")(value);
          }}
          error={state.errors.endTime}
          errorMessage={state.errors.endTime}
        />
      </Box>

      <Box sx={styles.assignShift}>
        <AppButton
          title={editMode ? "Update" : "Add"}
          width={"77px"}
          height={"34px"}
          fontSize={"15px"}
          // disabled={state.staffMember?.id === 0 || state.shiftType === "" || state.startTime === "" || state.endTime === "" || state.offDays?.length === 0}
          onClick={() => {
            editMode ? editShift() : addShift();
          }}
        />
      </Box>
    </Box>
  );
};

export default AssignShiftContent;
